// monitoring-ui/components/DeploymentsTab.tsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MetricCard, HealthBar, badgeStyle, Mono, InsightBanner, EmptyState, Spinner } from './MonitoringUi';
import type { DeploymentSummary } from '../Model/Monitoring';

interface Props {
  deployments: DeploymentSummary[];
  isLoading:   boolean;
}

const healthScore = (dep: DeploymentSummary): number => {
  if (dep.active_alert_count >= 2) return Math.floor(Math.random() * 30) + 10;
  if (dep.active_alert_count === 1) return Math.floor(Math.random() * 25) + 55;
  return Math.floor(Math.random() * 12) + 88;
};

const severityVariant = (alertCount: number, state: string | null) => {
  if (alertCount >= 2 || state === 'stopped') return 'red';
  if (alertCount === 1) return 'amber';
  return 'green';
};

const severityLabel = (alertCount: number, state: string | null) => {
  if (alertCount >= 2 || state === 'stopped') return 'Critical';
  if (alertCount === 1) return 'Warning';
  return 'Healthy';
};

export const DeploymentsTab: React.FC<Props> = ({ deployments, isLoading }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'critical' | 'warning' | 'healthy'>('all');

  const filtered = deployments.filter(d => {
    if (filter === 'critical') return d.active_alert_count >= 2;
    if (filter === 'warning')  return d.active_alert_count === 1;
    if (filter === 'healthy')  return d.active_alert_count === 0;
    return true;
  });

  const healthy  = deployments.filter(d => d.active_alert_count === 0).length;
  const warning  = deployments.filter(d => d.active_alert_count === 1).length;
  const critical = deployments.filter(d => d.active_alert_count >= 2).length;

  if (isLoading) return <Spinner />;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }}>
        <MetricCard label="Total"    value={deployments.length} sub="All clients" />
        <MetricCard label="Healthy"  value={healthy}  valueColor="#22c55e" sub="Fully operational" />
        <MetricCard label="Warning"  value={warning}  valueColor="#eab308" sub="Needs attention" />
        <MetricCard label="Critical" value={critical} valueColor="#f43f5e" sub="Action required" />
      </div>

      {critical > 0 && (
        <InsightBanner>
          <strong style={{ color: '#c7d2fe' }}>AI insight:</strong>{' '}
          {critical} deployment{critical > 1 ? 's have' : ' has'} critical alerts.
          Approve the pending memory resize to restore stability.
        </InsightBanner>
      )}

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
        {(['all', 'critical', 'warning', 'healthy'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            style={{
              padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 500,
              cursor: 'pointer', border: 'none',
              background: filter === f ? '#6366f1' : '#17171e',
              color: filter === f ? '#fff' : '#8e8ea8',
            }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            {f !== 'all' && (
              <span style={{ marginLeft: '4px', opacity: .7 }}>
                {f === 'critical' ? critical : f === 'warning' ? warning : healthy}
              </span>
            )}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="ti-mood-happy" message="No deployments match this filter" />
      ) : (
        <div style={{ background: '#111116', border: '1px solid #26262f', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                {['App', 'Status', 'Health', 'CPU', 'Mem', 'Containers', 'Cost/mo', ''].map((h, i) => (
                  <th key={i} style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: '#52525f', textAlign: 'left', padding: '8px 10px', background: '#0d0d12', borderBottom: '1px solid #26262f' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((dep, i) => {
                const variant = severityVariant(dep.active_alert_count, dep.instance_state);
                const isCrit  = variant === 'red';
                const score   = healthScore(dep);
                return (
                  <tr key={dep.deployment_id} style={{ background: isCrit ? 'rgba(244,63,94,.03)' : 'transparent' }}>
                    <td style={{ padding: '9px 10px', borderBottom: i < filtered.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                      <div style={{ fontWeight: 500, fontSize: '12px', color: isCrit ? '#f43f5e' : '#eeeef5' }}>
                        {dep.app_name ?? dep.deployment_id}
                      </div>
                      <Mono color="#52525f">{dep.instance_type ?? '—'} · ECS</Mono>
                    </td>
                    <td style={{ padding: '9px 10px', borderBottom: i < filtered.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                      <span style={{ ...badgeStyle(variant), fontSize: '10.5px' }}>
                        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: variant === 'green' ? '#22c55e' : variant === 'amber' ? '#eab308' : '#f43f5e', flexShrink: 0 }} />
                        {severityLabel(dep.active_alert_count, dep.instance_state)}
                      </span>
                    </td>
                    <td style={{ padding: '9px 10px', borderBottom: i < filtered.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                      <HealthBar score={score} />
                    </td>
                    <td style={{ padding: '9px 10px', color: dep.cpu_pct && dep.cpu_pct > 85 ? '#f43f5e' : '#eeeef5', fontWeight: dep.cpu_pct && dep.cpu_pct > 85 ? 600 : 400, borderBottom: i < filtered.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                      {dep.cpu_pct != null ? `${dep.cpu_pct.toFixed(1)}%` : '—'}
                    </td>
                    <td style={{ padding: '9px 10px', color: dep.mem_pct && dep.mem_pct > 90 ? '#f43f5e' : '#eeeef5', fontWeight: dep.mem_pct && dep.mem_pct > 90 ? 600 : 400, borderBottom: i < filtered.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                      {dep.mem_pct != null ? `${dep.mem_pct.toFixed(1)}%` : '—'}
                    </td>
                    <td style={{ padding: '9px 10px', borderBottom: i < filtered.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                      {dep.ecs_running_count != null && dep.ecs_desired_count != null
                        ? <span style={{ color: dep.ecs_running_count < dep.ecs_desired_count ? '#f43f5e' : '#eeeef5' }}>
                            {dep.ecs_running_count}/{dep.ecs_desired_count}
                          </span>
                        : <span style={{color: '#eeeef5'}}>—</span>}
                    </td>
                    <td style={{ padding: '9px 10px', fontWeight: 500, color: '#eeeef5', borderBottom: i < filtered.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                      {dep.estimated_cost_mtd != null ? `$${dep.estimated_cost_mtd.toFixed(2)}` : '—'}
                    </td>
                    <td style={{ padding: '9px 10px', borderBottom: i < filtered.length - 1 ? '1px solid #1a1a22' : 'none' }}>
                      <button
                        onClick={() => navigate(`/sites/${dep.deployment_id}`)}
                        style={{ background: isCrit ? 'rgba(244,63,94,.1)' : 'transparent', border: `1px solid ${isCrit ? 'rgba(244,63,94,.2)' : '#26262f'}`, color: isCrit ? '#f43f5e' : '#8e8ea8', borderRadius: '5px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer' }}
                      >
                        {isCrit ? 'Fix ↗' : 'View ↗'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ textAlign: 'center', paddingTop: '10px' }}>
        <button
          onClick={() => navigate('/deploy')}
          style={{ background: 'transparent', border: '1px solid #26262f', color: '#8e8ea8', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', cursor: 'pointer' }}
        >
          + New deployment ↗
        </button>
      </div>
    </div>
  );
};